import {
  Badge,
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  ClickAwayListener,
  Divider,
  Fade,
  IconButton,
  Link,
  Popper,
  Tooltip,
  Typography,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import React, { BaseSyntheticEvent, useContext, useEffect, useCallback } from "react";
import { makeStyles } from "@mui/styles";
import { useHistory } from "react-router-dom";
import { NotificationList } from "./NotificationList";
import { UserNotification } from "@backend-types";
import Axios from "../../../services/axios";
import { UserContext } from "../../../context/UserContext";
import { NotificationCategory, UserCtx } from "../../../interfaces";
import { WebsocketContext } from "../../../context/WebSocketContext";
import { generateGtmEvent } from "../../../Helpers";
import { useDidMountEffect } from "../../../hooks/useDidMount";
import { SettingsOutlined } from "@mui/icons-material";

const useStyles = makeStyles((theme) => ({
  title: {
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: "20px",
  },
  actionButton: {
    color: theme.palette.text.secondary,
  },
}));

const NOTIFICATION_CATEGORY: NotificationCategory[] = [
  {
    id: "1",
    name: "New",
    whiteSpace: "nowrap",
    filter: (notification: UserNotification) => !notification.is_read,
  },
  {
    id: "2",
    name: "Recent",
    whiteSpace: "nowrap",
    filter: (notification: UserNotification) => notification.is_read,
  },
];

interface AlertBellIconProps {
  notifications?: UserNotification[];

  isNotificationEnabled: boolean;
}

export const AlertBellIcon = (props: AlertBellIconProps) => {
  const classes = useStyles();
  const history = useHistory();
  const userCtx = useContext(UserContext) as UserCtx;
  const { isNotificationEnabled = true } = props;
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const { data } = useContext(WebsocketContext);

  const [settingsMenuOpen, setSettingsMenuOpen] = React.useState(false);
  // const [settingsMenuAnchorEl, setSettingsMenuAnchorEl] =
  //   React.useState<HTMLElement | null>(null);

  const [showDot, setShowDot] = React.useState(false);

  const [notifications, setNotifications] = React.useState<UserNotification[]>(
    props.notifications || []
  );
  const [isNewNotification, setIsNewNotification] = React.useState(false);

  const HandleDataLayer = (
    event: string,
    action: string,
    region: string,
    section: string,
    text: string,
    type?: string
  ) => {
    generateGtmEvent({
      event: event,
      action: action,
      name: "onclick",
      type: type ? type : "click",
      region: region,
      section: section,
      text: text,
    });
  };

  useEffect(() => {
    setIsNewNotification(
      notifications.filter((notification: UserNotification) => !notification.is_read)
        .length > 0
    );
  }, [notifications]);

  useEffect(() => {
    setShowDot(isNewNotification);
  }, [isNewNotification]);

  useEffect(() => {
    // @ts-ignore
    if (data && data.id) {
      let isNotificationExist = notifications.find(
        // @ts-ignore
        (notification: UserNotification) => notification.id === data.id
      );
      if (isNotificationExist) {
        return;
      }
      setNotifications((prev) => {
        if (prev.length === 10) {
          prev.pop();
        }
        prev.splice(0, 0, data as UserNotification);
        return [...prev];
      });
    }
  }, [data, notifications]);

  const markAllAsRead = useCallback(async () => {
    if (open) {
      try {
        let response = await Axios.patch("notifications");
        setShowDot(false);
        setIsNewNotification(false);
        if (response.data.refresh) {
          setNotifications(response.data.results);
          userCtx.setDbUser({
            ...userCtx.dbUser,
            user_notifications: response.data.results,
          }); // setting back the latest notification state to context
        }
      } catch (e) {
        console.log(e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // const handleToggleNotifications = async () => {
  //   try {
  //     await userCtx.updateDbUser(userCtx.dbUser.id, {
  //       notifications_enabled: !userCtx.dbUser.notifications_enabled,
  //     });
  //   } catch (e) {
  //     console.error(e);
  //   }
  // };

  const handleClose = useCallback(() => {
    // setSettingsMenuAnchorEl(null);
    setSettingsMenuOpen(false);
    setAnchorEl(null);
    setOpen(false);
    markAllAsRead();
  }, [markAllAsRead]);

  const handleSettingsClose = () => {
    setSettingsMenuOpen(false);
  };

  const handleClickIcon = (event: BaseSyntheticEvent | MouseEvent | TouchEvent) => {
    setAnchorEl(event.currentTarget);
    setOpen((prevOpen) => !prevOpen);
    // setSettingsMenuAnchorEl(null);
    setSettingsMenuOpen(false);
    markAllAsRead();
  };

  useDidMountEffect(() => {
    HandleDataLayer(
      "collapse",
      open ? "open" : "close",
      "navbar",
      "main navbar",
      "notifications"
    );
  }, [open]);

  useDidMountEffect(() => {
    HandleDataLayer(
      "collapse",
      settingsMenuOpen ? "open" : "close",
      "navbar",
      "notifications",
      "settings"
    );
  }, [settingsMenuOpen]);

  useEffect(() => {
    const escFunc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (settingsMenuOpen) {
          handleSettingsClose();
        } else if (open) {
          handleClose();
        }
      }
    };
    document.addEventListener("keyup", escFunc, false);

    return () => {
      document.removeEventListener("keyup", escFunc, false);
    };
  }, [handleClose, open, settingsMenuOpen]);

  return (
    <>
      <ClickAwayListener onClickAway={handleClose}>
        <Badge
          invisible={!(showDot && isNotificationEnabled)}
          color="primary"
          variant="dot"
          sx={{
            "& .MuiBadge-dot": {
              width: 8,
              height: 8,
              top: "5px",
              right: "10px",
            },
          }}>
          <Tooltip title="Notifications" placement="bottom" arrow>
            <IconButton
              sx={{ width: "44px", height: "44px" }}
              aria-label="notifications"
              onClick={handleClickIcon}>
              <NotificationsIcon width={20} height={20} sx={{ color: "#787676" }} />
            </IconButton>
          </Tooltip>
          <Popper
            id={"popper-d"}
            open={open}
            placement={"bottom-start"}
            anchorEl={anchorEl}
            sx={{ marginTop: "5px !important", zIndex: "4998 !important" }}
            transition>
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={350}>
                <Card
                  sx={{
                    maxWidth: "404px",
                    borderRadius: "8px",
                    boxShadow: "0px 8px 12px #00000019",
                    padding: "24px 0",
                  }}>
                  <CardHeader
                    sx={{ padding: "0px 24px 8px 24px" }}
                    title={
                      <>
                        <Typography variant="h6" className={classes.title}>
                          Notifications
                        </Typography>
                        {!isNotificationEnabled && (
                          <Typography
                            variant={"body2"}
                            sx={{
                              font: "normal normal normal 16px/24px Roboto;",
                              color: "#5D5D5D;",
                              letterSpacing: "0px",
                              marginTop: "20px",
                              marginBottom: "8px",
                            }}>
                            Your notifications are turned off
                          </Typography>
                        )}
                      </>
                    }
                    action={
                      <ClickAwayListener onClickAway={handleSettingsClose}>
                        <Box>
                          {/* This empty/invisible IconButton traps the focus and allows you to hit tab to go to actual IconButton below */}
                          <IconButton
                            autoFocus
                            sx={{ width: 0, height: 0, opacity: 0 }}></IconButton>

                          <Tooltip
                            placement="bottom-end"
                            title="Settings"
                            arrow
                            role="tooltip"
                            describeChild>
                            <IconButton
                              onClick={(event) => {
                                setOpen(false);
                                setAnchorEl(null);
                                // setSettingsMenuAnchorEl(null);
                                setSettingsMenuOpen(false);
                                history.push({
                                  pathname: "/notifications/settings",
                                });
                                // setSettingsMenuAnchorEl(event.currentTarget);
                                // setSettingsMenuOpen(!settingsMenuOpen);
                                generateGtmEvent({
                                  event: "link",
                                  action: "click",
                                  name: "onclick",
                                  type: "internal link",
                                  region: "main content",
                                  section: "notifications",
                                  text: "settings icon",
                                });
                              }}
                              aria-label="notification settings"
                              className={classes.actionButton}>
                              <SettingsOutlined />
                            </IconButton>
                          </Tooltip>
                          {/*<Popper*/}
                          {/*  id={"popper-settings-menu"}*/}
                          {/*  open={settingsMenuOpen}*/}
                          {/*  placement={"bottom-end"}*/}
                          {/*  anchorEl={settingsMenuAnchorEl}*/}
                          {/*  sx={{*/}
                          {/*    marginTop: "5px !important",*/}
                          {/*    zIndex: "4999 !important",*/}
                          {/*  }}*/}
                          {/*  transition>*/}
                          {/*  {({ TransitionProps }) => (*/}
                          {/*    <Fade {...TransitionProps} timeout={350}>*/}
                          {/*      <Card*/}
                          {/*        sx={{*/}
                          {/*          border: "2px solid #DEDEDE",*/}
                          {/*          borderRadius: "8px",*/}
                          {/*          marginTop: "-8px",*/}
                          {/*          marginRight: "16px",*/}
                          {/*        }}>*/}
                          {/*        /!* This empty/invisible IconButton traps the focus and allows you to hit tab to go to actual IconButton below *!/*/}
                          {/*        <IconButton*/}
                          {/*          autoFocus*/}
                          {/*          sx={{*/}
                          {/*            width: 0,*/}
                          {/*            height: 0,*/}
                          {/*            opacity: 0,*/}
                          {/*            padding: 0,*/}
                          {/*          }}></IconButton>*/}
                          {/*        <Button*/}
                          {/*          disableRipple*/}
                          {/*          sx={{*/}
                          {/*            height: "60px",*/}
                          {/*            cursor: "pointer",*/}
                          {/*            padding: "18px !important",*/}
                          {/*            borderRadius: "8px",*/}
                          {/*            color: "#787676",*/}
                          {/*            "&:focus-visible": {*/}
                          {/*              border: "2px solid black",*/}
                          {/*              "& .fa-regular": {*/}
                          {/*                display: isNotificationEnabled*/}
                          {/*                  ? "none"*/}
                          {/*                  : "inline-block",*/}
                          {/*              },*/}
                          {/*              "& .fa-solid": {*/}
                          {/*                display: isNotificationEnabled*/}
                          {/*                  ? "inline-block"*/}
                          {/*                  : "none",*/}
                          {/*              },*/}
                          {/*            },*/}
                          {/*            "& .fa-regular": {*/}
                          {/*              display: isNotificationEnabled*/}
                          {/*                ? "inline-block"*/}
                          {/*                : "none",*/}
                          {/*            },*/}
                          {/*            "& .fa-solid": {*/}
                          {/*              display: isNotificationEnabled*/}
                          {/*                ? "none"*/}
                          {/*                : "inline-block",*/}
                          {/*            },*/}
                          {/*            "&:hover": {*/}
                          {/*              "& .fa-regular": {*/}
                          {/*                display: isNotificationEnabled*/}
                          {/*                  ? "none"*/}
                          {/*                  : "inline-block",*/}
                          {/*              },*/}
                          {/*              "& .fa-solid": {*/}
                          {/*                display: isNotificationEnabled*/}
                          {/*                  ? "inline-block"*/}
                          {/*                  : "none",*/}
                          {/*              },*/}
                          {/*            },*/}
                          {/*          }}*/}
                          {/*          onClick={handleToggleNotifications}*/}
                          {/*          role="button"*/}
                          {/*          tabIndex={0}*/}
                          {/*          onKeyDown={(event) => {*/}
                          {/*            if (event.key === "Enter") {*/}
                          {/*              handleToggleNotifications();*/}
                          {/*            }*/}
                          {/*          }}>*/}
                          {/*          <i className="fa-regular fa-bell-slash"></i>*/}
                          {/*          <i className="fa-solid fa-bell-slash"></i>*/}
                          {/*          <Typography*/}
                          {/*            component="span"*/}
                          {/*            sx={{*/}
                          {/*              marginLeft: "12px",*/}
                          {/*              fontSize: "1rem",*/}
                          {/*              font: "normal normal normal 16px/20px Roboto",*/}
                          {/*            }}>*/}
                          {/*            Turn {isNotificationEnabled ? "off" : "on"}{" "}*/}
                          {/*            notifications*/}
                          {/*          </Typography>*/}
                          {/*        </Button>*/}
                          {/*      </Card>*/}
                          {/*    </Fade>*/}
                          {/*  )}*/}
                          {/*</Popper>*/}
                        </Box>
                      </ClickAwayListener>
                    }
                  />
                  <Divider
                    variant={"middle"}
                    sx={{ marginLeft: "1.5rem", marginRight: "1.5rem" }}
                  />
                  <CardContent
                    sx={{
                      overflow: "auto",
                      padding: " 0px 24px",
                      maxHeight: "455px",
                      "&::-webkit-scrollbar": {
                        width: "0.4em",
                      },
                      "&::-webkit-scrollbar-track": {
                        boxShadow: "inset 0 0 6px rgba(0,0,0,0.00)",
                        webkitBoxShadow: "inset 0 0 6px rgba(0,0,0,0.00)",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "rgba(0,0,0,.1)",
                      },
                    }}>
                    <NotificationList
                      noNotificationMessage={
                        "No recent alerts. use ‘View all’ to view your history."
                      }
                      showCategoryDivider={false}
                      categoryFontSize={"20px"}
                      notificationCategory={NOTIFICATION_CATEGORY}
                      notifications={notifications}
                    />
                  </CardContent>
                  <Divider
                    variant={"middle"}
                    sx={{ marginLeft: "1.5rem", marginRight: "1.5rem" }}
                  />
                  <CardActions sx={{ paddingTop: "16px" }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100%",
                      }}>
                      <Link
                        component={"button"}
                        tabIndex={0}
                        onClick={() => {
                          setOpen(false);
                          setAnchorEl(null);
                          // setSettingsMenuAnchorEl(null);
                          setSettingsMenuOpen(false);
                          history.push({
                            pathname: "/notifications",
                          });
                          generateGtmEvent({
                            event: "link",
                            action: "click",
                            name: "onclick",
                            type: "internal link",
                            region: "main content",
                            section: `notifications`,
                            text: "view all",
                          });
                        }}
                        variant="body1">
                        View all
                      </Link>
                    </Box>
                  </CardActions>
                </Card>
              </Fade>
            )}
          </Popper>
        </Badge>
      </ClickAwayListener>
    </>
  );
};
